import { useContext, useEffect, useState } from "react";

import { apiRequest, UserContext } from "./helpers";

const languageList = [];

const LANGUAGES_STORAGE_KEY = "supported_languages_v2";
const LANGUAGES_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days, matching server-side Redis TTL

let isCacheFresh = false;
let inFlightRequest = null;

const normalizeLanguageList = (entries) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  return entries
    .map((language) => ({
      code: String(
        (language && (language.code || language.language)) || ""
      ).trim(),
      name: String((language && language.name) || "").trim(),
    }))
    .filter((language) => language.code && language.name)
    .sort((a, b) => a.name.localeCompare(b.name));
};

const readCachedLanguageList = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return { entries: [], fresh: false };
  }

  try {
    const serialized = window.localStorage.getItem(LANGUAGES_STORAGE_KEY);
    if (!serialized) {
      return { entries: [], fresh: false };
    }
    const { entries, expiresAt } = JSON.parse(serialized);

    if (!Array.isArray(entries) || entries.length === 0) {
      return { entries: [], fresh: false };
    }

    if (!expiresAt || Date.now() > expiresAt) {
      window.localStorage.removeItem(LANGUAGES_STORAGE_KEY);
      return { entries, fresh: false };
    }

    return { entries, fresh: true };
  } catch (_error) {
    return { entries: [], fresh: false };
  }
};

const writeCachedLanguageList = (entries) => {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    const normalized = normalizeLanguageList(entries);
    window.localStorage.setItem(
      LANGUAGES_STORAGE_KEY,
      JSON.stringify({
        entries: normalized,
        expiresAt: Date.now() + LANGUAGES_CACHE_TTL_MS,
      })
    );
  } catch (_error) {
    // Ignore storage errors and keep in-memory values.
  }
};

const { entries: cachedEntries, fresh } = readCachedLanguageList();
isCacheFresh = fresh;

const setLanguageState = (entries) => {
  languageList.splice(0, languageList.length, ...entries);
};

setLanguageState(cachedEntries.length > 0 ? cachedEntries : languageList);

const fetchSupportedLanguages = () => {
  if (inFlightRequest) {
    return inFlightRequest;
  }

  inFlightRequest = apiRequest({
    method: "GET",
    endpoint: `v2/supported-languages`,
  })
    .then((response) => {
      const entries = normalizeLanguageList(response);
      if (entries.length > 0) {
        setLanguageState(entries);
        writeCachedLanguageList(entries);
      }
      isCacheFresh = true;
      return entries.length > 0 ? entries : [...languageList];
    })
    .catch(() => {
      return [...languageList];
    })
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
};

const loadSupportedLanguages = () => {
  if (isCacheFresh) {
    return Promise.resolve([...languageList]);
  }

  return fetchSupportedLanguages();
};

const useSupportedLanguages = () => {
  const { user } = useContext(UserContext);
  const [languageData, setLanguageData] = useState(() => ({
    languageList: [...languageList],
  }));

  useEffect(() => {
    if (user && user.company && user.company.id) {
      loadSupportedLanguages().then((entries) => {
        setLanguageData({ languageList: entries || [] });
      });
    }
  }, [user]);

  return languageData;
};

const __resetForTesting = () => {
  isCacheFresh = false;
  inFlightRequest = null;
  languageList.splice(0);
};

export { loadSupportedLanguages, useSupportedLanguages };

// Exported for testing only — do not import this in application code.
export { __resetForTesting };
